import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  CircularProgress,
  Divider,
  Stack,
  alpha,
  useTheme as useThemeV5,
  Card,
  CardContent,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";

// Ícones Modernos
import {
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Person as PersonIcon,
  Devices as DevicesIcon,
  Queue as QueueIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateRangeIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";

import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload;
    const newItems = [];
    const newState = [...state];

    invoices.forEach((item) => {
      const index = newState.findIndex((i) => i.id === item.id);
      if (index !== -1) {
        newState[index] = item;
      } else {
        newItems.push(item);
      }
    });

    return [...newState, ...newItems];
  }

  if (action.type === "UPDATE_INVOICE") {
    const invoice = action.payload;
    const index = state.findIndex((i) => i.id === invoice.id);

    if (index !== -1) {
      const newState = [...state];
      newState[index] = invoice;
      return newState;
    } else {
      return [invoice, ...state];
    }
  }

  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const Financeiro = () => {
  const themeV4 = useThemeV4();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeV4.palette.type,
          primary: themeV4.palette.primary,
          secondary: themeV4.palette.secondary,
          error: themeV4.palette.error,
          warning: themeV4.palette.warning,
          info: themeV4.palette.info,
          success: themeV4.palette.success,
          text: themeV4.palette.text,
          background: themeV4.palette.background,
          divider: themeV4.palette.divider,
        },
        shape: { borderRadius: 16 },
      }),
    [themeV4],
  );

  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [invoices, dispatch] = useReducer(reducer, []);
  const [companyPlan, setCompanyPlan] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    const fetchCompanyPlan = async () => {
      try {
        if (user?.companyId) {
          const companyResponse = await api.get(`/companies/${user.companyId}`);
          if (companyResponse.data?.planId) {
            const planResponse = await api.get(
              `/plans/${companyResponse.data.planId}`,
            );
            setCompanyPlan(planResponse.data);
          }
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchCompanyPlan();
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const fetchInvoices = async () => {
      try {
        const { data } = await api.get("/invoices/all", {
          params: { pageNumber },
        });
        dispatch({ type: "LOAD_INVOICES", payload: data });
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [pageNumber]);

  const handleOpenContactModal = (invoice) => {
    const invoiceWithPlanValue = {
      ...invoice,
      value: companyPlan?.amount
        ? parseFloat(companyPlan.amount)
        : invoice.value,
    };
    setSelectedInvoice(invoiceWithPlanValue);
    setContactModalOpen(true);
  };

  const getInvoiceStatus = (record) => {
    const status = record.status;
    const isOverdue = moment(record.dueDate).isBefore(moment(), "day");

    if (status === "paid") {
      return {
        text: "Pago",
        color: "success",
        icon: <CheckCircleIcon fontSize="small" />,
      };
    }
    if (isOverdue) {
      return {
        text: "Vencido",
        color: "error",
        icon: <ErrorIcon fontSize="small" />,
      };
    }
    return {
      text: "Pendente",
      color: "warning",
      icon: <HourglassEmptyIcon fontSize="small" />,
    };
  };

  const stats = useMemo(() => {
    const total = invoices.length;
    const paid = invoices.filter((i) => i.status === "paid").length;
    const pending = invoices.filter(
      (i) =>
        i.status !== "paid" && !moment(i.dueDate).isBefore(moment(), "day"),
    ).length;
    const overdue = invoices.filter(
      (i) => i.status !== "paid" && moment(i.dueDate).isBefore(moment(), "day"),
    ).length;
    return { total, paid, pending, overdue };
  }, [invoices]);

  const renderSummaryCards = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {[
        {
          label: "Total de Faturas",
          value: stats.total,
          color: "primary",
          icon: <ReceiptIcon />,
        },
        {
          label: "Faturas Pagas",
          value: stats.paid,
          color: "success",
          icon: <CheckCircleIcon />,
        },
        {
          label: "Pendentes",
          value: stats.pending,
          color: "warning",
          icon: <HourglassEmptyIcon />,
        },
        {
          label: "Vencidas",
          value: stats.overdue,
          color: "error",
          icon: <ErrorIcon />,
        },
      ].map((s, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette[s.color].main, 0.1),
                    color: `${s.color}.main`,
                    borderRadius: "12px",
                    width: 48,
                    height: 48,
                  }}
                >
                  {s.icon}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="800">
                    {s.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    fontWeight="600"
                  >
                    {s.label}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          bgcolor:
            theme.palette.mode === "light" ? "#f5f5f5" : "background.default",
          minHeight: "100vh",
          pb: 8,
        }}
      >
        <SubscriptionModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          Invoice={selectedInvoice}
        />

        <Container maxWidth="xl" sx={{ pt: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                fontWeight="800"
                color="primary"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <WalletIcon fontSize="large" /> Financeiro
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Gerencie suas faturas, planos e pagamentos
              </Typography>
            </Box>
          </Box>

          {renderSummaryCards()}

          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="800" color="textPrimary">
              Histórico de Faturas
            </Typography>
            {hasMore && !loading && (
              <Button
                onClick={() => setPageNumber((prev) => prev + 1)}
                variant="outlined"
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                Carregar mais
              </Button>
            )}
          </Box>

          <Grid container spacing={3}>
            {invoices.map((invoice) => {
              const status = getInvoiceStatus(invoice);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={invoice.id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all 0.3s ease",
                      position: "relative",
                      overflow: "visible",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
                        borderColor: "primary.light",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -12,
                        right: 16,
                        zIndex: 1,
                      }}
                    >
                      <Chip
                        label={status.text}
                        color={status.color}
                        size="small"
                        icon={status.icon}
                        sx={{ fontWeight: "bold", borderRadius: "8px", px: 1 }}
                      />
                    </Box>

                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          fontWeight="bold"
                        >
                          # FATURA {invoice.id}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight="900"
                          color="textPrimary"
                          sx={{ mt: 1 }}
                        >
                          {companyPlan?.amount
                            ? parseFloat(companyPlan.amount).toLocaleString(
                                "pt-br",
                                { style: "currency", currency: "BRL" },
                              )
                            : invoice.value.toLocaleString("pt-br", {
                                style: "currency",
                                currency: "BRL",
                              })}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mt: 0.5 }}
                        >
                          Referente ao plano:{" "}
                          <Box
                            component="span"
                            fontWeight="bold"
                            color="primary.main"
                          >
                            {companyPlan?.name || "Premium"}
                          </Box>
                        </Typography>
                      </Box>

                      <Divider sx={{ mb: 3 }} />

                      <Stack spacing={2}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: "primary.main",
                              width: 32,
                              height: 32,
                            }}
                          >
                            <DateRangeIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              Vencimento
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {moment(invoice.dueDate).format("DD/MM/YYYY")}
                            </Typography>
                          </Box>
                        </Box>

                        <Grid container spacing={1}>
                          {[
                            {
                              icon: <PersonIcon sx={{ fontSize: 16 }} />,
                              label: `${companyPlan?.users || 0} Usuários`,
                            },
                            {
                              icon: <DevicesIcon sx={{ fontSize: 16 }} />,
                              label: `${companyPlan?.connections || 0} Conexões`,
                            },
                          ].map((item, idx) => (
                            <Grid item xs={6} key={idx}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: "text.secondary",
                                }}
                              >
                                {item.icon}
                                <Typography variant="caption" fontWeight="600">
                                  {item.label}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Stack>

                      <Box sx={{ mt: 4 }}>
                        {status.text !== "Pago" ? (
                          <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<PaymentIcon />}
                            onClick={() => handleOpenContactModal(invoice)}
                            sx={{
                              borderRadius: "50px",
                              py: 1.2,
                              fontWeight: "bold",
                              textTransform: "none",
                              boxShadow: "none",
                              "&:hover": { boxShadow: theme.shadows[4] },
                            }}
                          >
                            Pagar Agora
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant="outlined"
                            color="success"
                            disabled
                            startIcon={<CheckCircleIcon />}
                            sx={{
                              borderRadius: "50px",
                              py: 1.2,
                              fontWeight: "bold",
                              textTransform: "none",
                              color: "success.main !important",
                              borderColor: "success.main !important",
                              opacity: 0.8,
                            }}
                          >
                            Pagamento Confirmado
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
            {loading && invoices.length === 0 && (
              <Box sx={{ width: "100%", mt: 8, textAlign: "center" }}>
                <CircularProgress />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 2 }}
                >
                  Carregando faturas...
                </Typography>
              </Box>
            )}
            {!loading && invoices.length === 0 && (
              <Box sx={{ width: "100%", mt: 8, textAlign: "center" }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 4, borderRadius: "20px", borderStyle: "dashed" }}
                >
                  <ReceiptIcon sx={{ fontSize: 64, color: "divider", mb: 2 }} />
                  <Typography variant="h6" color="textSecondary">
                    Nenhuma fatura encontrada
                  </Typography>
                </Paper>
              </Box>
            )}
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Financeiro;
